import moment from 'moment';
import { QRCodeSVG } from 'qrcode.react';
import React, { useEffect, useState, useRef, useCallback } from "react";
import bluetoothService from '../services/bluetoothService';
import { sendAnalyticsEvent } from '../services/analyticsService';

const formatDurationSeconds = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

const getModeIconClass = (mode) => {
    const modeMap = {
        'CAR': 'drive',
        'WALK': 'walk',
        'BUS': 'bus',
        'RAIL': 'rail',
        'TRAM': 'tram',
        'SUBWAY': 'subway',
        'METRO': 'metro'
    };
    
    return modeMap[mode?.toUpperCase()] || mode?.toLowerCase() || 'walk';
};

const getDestinationText = (leg, legIndex, allLegs, finalDestination) => {
    // For transit legs with stops, use the stop name
    if (leg.to?.stop?.name) {
        return leg.to.stop.name;
    }
    
    // For walking/driving legs, find the next transit leg
    const nextTransitLeg = allLegs.slice(legIndex + 1).find(nextLeg => nextLeg.trip?.gtfsId);
    
    if (nextTransitLeg?.from?.stop?.name) {
        return nextTransitLeg.from.stop.name;
    }
    
    // If no next transit leg, check if this is the very last leg
    const isLastLeg = legIndex === allLegs.length - 1;
    
    if (isLastLeg) {
        // Use final journey destination for the very last leg
        return finalDestination || leg.to?.name || 'Destination';
    } else {
        // Use the leg's own destination as specified in JP response
        return leg.to?.name || 'Destination';
    }
};


// Generate QR code by calling the backend API
export async function generateQrCode(nextPlan, selectedRoute, deviceId = null, gtfsTripId = null, selectedMode = 'RAIL') {
    try {
        // Get device ID from connected device if not provided
        const connectedDevice = bluetoothService.getConnectedDevice();
        const finalDeviceId = deviceId || connectedDevice?.id || 'unknown';
        
        // Extract GTFS trip ID from journey data if available
        const finalGtfsTripId = gtfsTripId || extractGtfsTripId(nextPlan);
        
        // Get the origin name using the same logic as the display
        const originName = getOriginNameFromJourney(nextPlan, selectedMode);
        console.log('generateQrCode - originName:', originName);
        
        // Create complete journey payload including ALL legs (walk, car, bus, etc.)
        const completeJourneyPayload = createCompleteJourneyPayload(nextPlan, selectedRoute, originName);
        console.log('generateQrCode - journeyPayload origin:', completeJourneyPayload?.origin);
        
        if (!completeJourneyPayload) {
            throw new Error('Failed to create complete journey payload');
        }

        const response = await fetch('https://iot.service.zpbt.uk/Ticket_Window_Device', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                journeyPayload: completeJourneyPayload,
                nextPlan: nextPlan, // Keep original for backward compatibility
                selectedRoute,
                deviceId: finalDeviceId,
                gtfsTripId: finalGtfsTripId
            })
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`QR code service responded with status ${response.status}: ${errorText}`);
        }

        const result = await response.json();
        return result.qrCodeUrl;
    } catch (error) {
        console.error('Error generating QR code:', error);
        throw error;
    }
}

// Extract GTFS trip ID from journey plan data
function extractGtfsTripId(nextPlan) {
    try {
        // Look for trip information in the journey legs
        if (nextPlan?.node?.legs) {
            for (const leg of nextPlan.node.legs) {
                if (leg.trip?.gtfsId) {
                    return leg.trip.gtfsId;
                }
                if (leg.trip?.routeShortName) {
                    return leg.trip.routeShortName;
                }
            }
        }
        return null;
    } catch (error) {
        console.error('Error extracting GTFS trip ID:', error);
        return null;
    }
}

// Helper function to determine stop code type based on mode and region
function getStopCodeType(mode, region = 'GB') {
    if (mode === "RAIL" && region === "GB") {
        return "CRS";
    } else if (mode === "WALK" || mode === "CAR" || mode === "BICYCLE") {
        return "GEOHASH";
    } else {
        return "GTFS";
    }
}

// Helper function to get origin name using the same logic as display
function getOriginNameFromJourney(nextPlan, selectedMode = 'RAIL') {
    if (selectedMode === 'BUS') {
        const transitLegs = nextPlan?.node?.legs?.filter(leg => leg.trip?.gtfsId) || [];
        if (transitLegs.length > 0) {
            const firstTransitLeg = transitLegs[0];
            return firstTransitLeg?.from?.stop?.name || firstTransitLeg?.from?.name;
        } else {
            return nextPlan?.node?.legs?.[0]?.from?.name || nextPlan?.node?.legs?.[0]?.from?.stop?.name;
        }
    } else {
        // For rail mode: use transit leg origin
        const transitLegs = nextPlan?.node?.legs?.filter(leg => leg.trip?.gtfsId) || [];
        if (transitLegs.length > 0) {
            const firstTransitLeg = transitLegs[0];
            return firstTransitLeg?.from?.stop?.name;
        } else {
            // Fallback to legacy behavior
            const legWithTrip = nextPlan?.node?.legs?.find(leg =>
                leg.trip?.stoptimesForDate?.[0]?.departureDelay !== undefined
            );
            return legWithTrip?.from?.stop?.name;
        }
    }
}

// Create complete journey payload including ALL legs (walk, car, bus, etc.)
function createCompleteJourneyPayload(nextPlan, selectedRoute, originName = null) {
    try {
        const legs = nextPlan?.node?.legs || [];
        if (legs.length === 0) {
            return null;
        }

        // Create journey legs including ALL modes
        const journeyLegs = legs.map((leg, index) => {
            const stopCodeType = getStopCodeType(leg.mode, "GB");
            const isFirstLeg = index === 0;
            const isLastLeg = index === legs.length - 1;

            // For GEOHASH legs (walk, car), encode coordinates
            let fromGeohashCode = '';
            let toGeohashCode = '';
            if (stopCodeType === "GEOHASH") {
                if (leg.from?.lat && leg.from?.lon) {
                    // Simple geohash encoding (you might want to use a proper geohash library)
                    fromGeohashCode = `${leg.from.lat.toFixed(6)},${leg.from.lon.toFixed(6)}`;
                }
                if (leg.to?.lat && leg.to?.lon) {
                    toGeohashCode = `${leg.to.lat.toFixed(6)},${leg.to.lon.toFixed(6)}`;
                }
            }

            return {
                stopCodeOrigin: stopCodeType === "GEOHASH" ? fromGeohashCode : 
                              stopCodeType === "GTFS" ? leg.from.stop?.gtfsId : leg.from.stop?.code,
                name: isFirstLeg ? (selectedRoute?.origin?.name || leg.from?.name) : 
                      (leg.from.stop?.name || leg.from?.name),
                stopCodeType: stopCodeType,
                stopCodeDestination: stopCodeType === "GEOHASH" ? toGeohashCode : 
                                   stopCodeType === "GTFS" ? leg.to.stop?.gtfsId : leg.to.stop?.code,
                stopCodeTypeDestination: stopCodeType,
                destinationName: isLastLeg ? (selectedRoute?.destination?.name || leg.to?.name) : 
                               (leg.to.stop?.name || leg.to?.name),
                legIndex: index,
                isOutbound: true,
                travelMode: leg.mode,
                startTime: moment(leg.from?.departure?.scheduledTime).unix(),
                endTime: moment(leg.to?.arrival?.scheduledTime).unix(),
                status: leg.status || 'ontime',
                tripId: leg.trip?.gtfsId || null,
                duration: leg.duration || 0,
                distance: leg.distance || 0
            };
        });

        // Create complete journey payload
        const journeyPayload = {
            origin: {
                name: originName || selectedRoute?.origin?.name || journeyLegs[0]?.name || '',
                stopCode: journeyLegs[0]?.stopCodeOrigin || '',
                stopCodeType: journeyLegs[0]?.stopCodeType || ''
            },
            destination: {
                name: selectedRoute?.destination?.name || journeyLegs[journeyLegs.length - 1]?.destinationName || '',
                stopCode: journeyLegs[journeyLegs.length - 1]?.stopCodeDestination || '',
                stopCodeType: journeyLegs[journeyLegs.length - 1]?.stopCodeTypeDestination || ''
            },
            legs: journeyLegs,
            totalDuration: nextPlan?.node?.duration || 0,
            startTime: moment(nextPlan?.node?.start).unix(),
            endTime: moment(nextPlan?.node?.end).unix(),
            status: nextPlan?.node?.status || 'ontime'
        };

        return journeyPayload;
    } catch (error) {
        console.error('Error creating complete journey payload:', error);
        return null;
    }
}


export function ShareScreen({ nextPlan, onClose, selectedRoute, selectedMode = 'RAIL', isStandalone = false, originDetails }) {
    const [qrCodeUrl, setQrCodeUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showEnlargedQr, setShowEnlargedQr] = useState(false);

    const [statusMessages, setStatusMessages] = useState([]);
    const [hasSentToDevice, setHasSentToDevice] = useState(false);
    const statusPanelRef = useRef(null);

    const addStatusMessage = useCallback((type, message) => {
        setStatusMessages(prev => [...prev, { type, message }]);
    }, []);

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        if (statusPanelRef.current) {
            statusPanelRef.current.scrollTop = statusPanelRef.current.scrollHeight;
        }
    }, [statusMessages]);

    // Derive journey data first (needed for hooks)
    const lastLegWithTrip = [...(nextPlan?.node?.legs || [])].reverse().find(leg =>
        leg.trip?.stoptimesForDate?.[0]?.departureDelay !== undefined
    );

    // Get origin and destination names, legs to show, and times based on mode
    const originName = getOriginNameFromJourney(nextPlan, selectedMode);
    let destinationName, legsToShow, startTime, endTime;
    
    if (selectedMode === 'BUS') {
        // For bus mode: use transit leg boundaries for destination
        const transitLegs = nextPlan?.node?.legs?.filter(leg => leg.trip?.gtfsId) || [];
        
        if (transitLegs.length > 0) {
            // Use last transit leg destination
            const lastTransitLeg = transitLegs[transitLegs.length - 1];
            destinationName = lastTransitLeg?.to?.stop?.name || lastTransitLeg?.to?.name;
        } else {
            // No transit legs, use journey end
            destinationName = nextPlan?.node?.legs?.[nextPlan?.node?.legs?.length - 1]?.to?.name || nextPlan?.node?.legs?.[nextPlan?.node?.legs?.length - 1]?.to?.stop?.name;
        }
        
        // For bus mode: show all legs including walking, use complete journey times
        legsToShow = nextPlan?.node?.legs?.filter(leg => leg.mode) || [];
        startTime = nextPlan?.node?.start ? moment(nextPlan.node.start) : null;
        endTime = nextPlan?.node?.end ? moment(nextPlan.node.end) : null;
    } else {
        // For rail mode: only show transit legs and use transit leg times (match display exactly)
        const transitLegs = nextPlan?.node?.legs?.filter(leg => leg.trip?.gtfsId) || [];
        legsToShow = transitLegs;
        
        if (transitLegs.length > 0) {
            const lastTransitLeg = transitLegs[transitLegs.length - 1];
            
            destinationName = lastTransitLeg?.to?.stop?.name;
            
            // Use ONLY transit leg times (excluding walking), matching what's displayed
            const firstTransitLeg = transitLegs[0];
            startTime = firstTransitLeg?.from?.departure?.scheduledTime ? moment(firstTransitLeg.from.departure.scheduledTime) : null;
            endTime = lastTransitLeg?.to?.arrival?.scheduledTime ? moment(lastTransitLeg.to.arrival.scheduledTime) : null;
        } else {
            // Fallback to legacy behavior if no transit legs found
            destinationName = lastLegWithTrip?.to?.stop?.name;
            startTime = nextPlan?.node?.start ? moment(nextPlan.node.start) : null;
            endTime = nextPlan?.node?.end ? moment(nextPlan.node.end) : null;
        }
    }

    // Send journey data to connected device
    const sendJourneyToDevice = useCallback(async () => {
        // Check if already sent to prevent duplicate calls
        if (hasSentToDevice) {
            return;
        }

        if (!bluetoothService.isConnected()) {
            addStatusMessage('warning', 'No device connected');
            return;
        }

        if (!selectedRoute || !startTime || !endTime) {
            addStatusMessage('error', 'Journey data not available');
            return;
        }

        setHasSentToDevice(true);
        addStatusMessage('info', 'Sending journey to device...');

        // Store original handler to restore later
        const originalHandler = bluetoothService.onDataReceived;
        let responseReceived = false;
        let responseTimeout = null;

        try {
            // Smart date formatting for device display
            const now = moment();
            const journeyDate = startTime.clone().startOf('day');
            const today = now.clone().startOf('day');
            const tomorrow = today.clone().add(1, 'day');
            
            let dateText;
            if (journeyDate.isSame(today)) {
                dateText = 'Today';
            } else if (journeyDate.isSame(tomorrow)) {
                dateText = 'Tomorrow';
            } else {
                dateText = startTime.format('dddd, MMM D');
            }

            // Format journey data - weather removed to save bandwidth
            const journeyData = {
                origin: originName || originDetails?.name || "Unknown Origin",
                destination: selectedRoute.destination.name,
                date: dateText,
                leaving_time: startTime.format('HH:mm'),
                arriving_time: endTime.format('HH:mm'),
                current_time: moment().format('HH:mm'),
                qr_url: qrCodeUrl || '',
                timestamp: new Date().toISOString()
            };

            // Set up response listener before sending data
            const handleDeviceResponse = (receivedData) => {
                console.log('ShareScreen received device response:', receivedData);
                
                // Device sends "OK" for successful journey data processing
                if (receivedData.includes('OK')) {
                    responseReceived = true;
                    
                    // Clear timeout since we got a response
                    if (responseTimeout) {
                        clearTimeout(responseTimeout);
                        responseTimeout = null;
                    }
                    
                    if (receivedData.trim() === 'OK') {
                        // Simple "OK" response for journey data
                        addStatusMessage('success', 'Device confirmed: Journey received and processed');
                        
                        // Send analytics for Bluetooth sharing
                        sendAnalyticsEvent('device_ticket-window_qr-shared', {
                            origin: originName,
                            destination: destinationName,
                            mode: selectedMode,
                            departureTime: startTime?.format('HH:mm'),
                            arrivalTime: endTime?.format('HH:mm'),
                            date: startTime?.format('YYYY-MM-DD'),
                            shareMethod: 'bluetooth',
                            isStandalone: false
                        });
                    } else if (receivedData.includes('Connection acknowledged')) {
                        addStatusMessage('success', 'Connection acknowledged');
                    } else if (receivedData.includes('Heartbeat')) {
                        addStatusMessage('info', 'Heartbeat acknowledged');
                    } else if (receivedData.includes('Reverted to default')) {
                        addStatusMessage('success', 'Reverted to default screen');
                    } else {
                        // Any other OK response
                        addStatusMessage('success', `Device: ${receivedData.trim()}`);
                    }
                    
                    // Restore original handler
                    bluetoothService.onDataReceived = originalHandler;
                } else if (receivedData.includes('ERROR')) {
                    responseReceived = true;
                    
                    // Clear timeout since we got a response
                    if (responseTimeout) {
                        clearTimeout(responseTimeout);
                        responseTimeout = null;
                    }
                    
                    addStatusMessage('error', `Device error: ${receivedData.trim()}`);
                    
                    // Restore original handler
                    bluetoothService.onDataReceived = originalHandler;
                }
            };
            
            // Set up response listener
            bluetoothService.onDataReceived = handleDeviceResponse;

            // Show that we're waiting for confirmation before sending
            addStatusMessage('info', 'Sending journey data and waiting for confirmation...');
            
            await bluetoothService.sendJourneyData(journeyData);
            
            // If we haven't received a response yet, set up timeout
            if (!responseReceived) {
                // Set a timeout to restore the original handler and show fallback message
                responseTimeout = setTimeout(() => {
                    if (!responseReceived) {
                        addStatusMessage('info', 'Journey sent (no device response received)');
                    }
                    bluetoothService.onDataReceived = originalHandler;
                }, 3000); // 3 second timeout
            }

        } catch (error) {
            console.error('Error sending journey data:', error);
            addStatusMessage('error', `Failed to send journey: ${error.message}`);
            // Reset hasSentToDevice on error to allow retry
            setHasSentToDevice(false);
            
            // Clean up timeout and restore original handler
            if (responseTimeout) {
                clearTimeout(responseTimeout);
            }
            bluetoothService.onDataReceived = originalHandler;
        }
    }, [selectedRoute, startTime, endTime, qrCodeUrl, addStatusMessage, hasSentToDevice, originDetails?.name, destinationName, originName, selectedMode]);

    // Generate QR code when plan changes
    useEffect(() => {
        if (!nextPlan?.node) {
            return;
        }
        
        // Reset sending state when plan changes
        setHasSentToDevice(false);
        
        const generateQrCodeUrl = async () => {
            try {
                setIsLoading(true);
                setQrCodeUrl(null); // Clear old QR code immediately
                const qrCodeUrl = await generateQrCode(nextPlan, selectedRoute, null, null, selectedMode);
                setQrCodeUrl(qrCodeUrl);
                
                // Send analytics for QR generation - capture current values at time of generation
                await sendAnalyticsEvent('device_ticket-window_qr-generated', {
                    origin: originName,
                    destination: destinationName,
                    mode: selectedMode,
                    departureTime: startTime?.format('HH:mm'),
                    arrivalTime: endTime?.format('HH:mm'),
                    date: startTime?.format('YYYY-MM-DD'),
                    qrUrl: qrCodeUrl,
                    isStandalone: isStandalone
                });
            } catch (error) {
                console.error('Error generating QR code:', error);
                addStatusMessage('error', `Failed to generate QR code: ${error.message}`);
                setQrCodeUrl(null); // Ensure no old QR code shows on error
            } finally {
                setIsLoading(false);
            }
        };

        generateQrCodeUrl();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nextPlan, selectedRoute, addStatusMessage, selectedMode]);

    // Auto-send to device when QR code is ready and Bluetooth is connected (skip in standalone mode)
    useEffect(() => {
        if (!isStandalone && qrCodeUrl && bluetoothService.isConnected() && !hasSentToDevice) {
            // Add a small delay to prevent multiple rapid calls
            const sendTimer = setTimeout(() => {
                sendJourneyToDevice();
            }, 100);
            
            return () => clearTimeout(sendTimer);
        }
    }, [qrCodeUrl, hasSentToDevice, sendJourneyToDevice, isStandalone, destinationName, endTime, originName, selectedMode, startTime]);

    // Register for centralized connection loss handling (no manual polling in standalone mode)
    useEffect(() => {
        if (isStandalone) {
            // Skip connection monitoring in standalone mode
            return;
        }
        
        // Connection loss is handled by parent App.js component
        // We don't need to override the handler here to avoid conflicts
        
        // Just listen for disconnection to update our local status
        const checkConnection = () => {
            if (!bluetoothService.isConnected()) {
                addStatusMessage('warning', 'Bluetooth connection lost');
            }
        };
        
        // Check connection status periodically but let parent handle navigation
        const connectionCheckInterval = setInterval(checkConnection, 5000);
        
        return () => {
            clearInterval(connectionCheckInterval);
        };
    }, [addStatusMessage, isStandalone]);

    if (!nextPlan?.node) {
        return null;
    }

    // Close handler - no longer sends revert signal (privacy timer handles this)
    const handleClose = async () => {
        onClose();
    };

    return (
        <div className="w-full h-full bg-white flex flex-col relative">
                {/* Header */}
                <div className="pt-4 sm:pt-8 px-4 sm:px-8 flex justify-between items-start mb-4 sm:mb-8">
                    <div className="flex-1 pr-2">
                        <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-black">
                            {originName} to {destinationName}
                        </h1>
                        <p className="text-sm sm:text-base lg:text-lg text-gray-600 mt-1">
                            {startTime.format('DD MMMM YYYY')}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="w-10 h-10 sm:w-12 sm:h-12 bg-black text-white rounded-full transition-colors flex items-center justify-center">
                        <i className="zip-icon-int_arrow_round_left text-2xl sm:text-3xl lg:text-4xl"></i>
                    </button>
                </div>

                {/* Main Content - Journey Details and QR Code */}
                <div className="px-4 sm:px-8 flex flex-col lg:flex-row gap-6">
                    {/* Left side - Journey Details */}
                    <div className="flex-1 lg:pr-8">
                        {(() => {
                            // Dynamic spacing based on number of legs (same logic as JourneySummary)
                            const getSpacingClass = (numLegs) => {
                                if (numLegs <= 2) return 'space-y-4 sm:space-y-6';
                                if (numLegs === 3) return 'space-y-3 sm:space-y-4';
                                if (numLegs === 4) return 'space-y-2 sm:space-y-3';
                                return 'space-y-1 sm:space-y-2'; // 5+ legs
                            };

                            // Dynamic padding based on number of legs (same logic as JourneySummary)
                            const getPaddingClass = (numLegs) => {
                                if (numLegs <= 2) return 'py-3 sm:py-4 lg:py-6';
                                if (numLegs === 3) return 'py-2 sm:py-3 lg:py-4';
                                if (numLegs === 4) return 'py-1 sm:py-2 lg:py-3';
                                return 'py-1 sm:py-1 lg:py-2'; // 5+ legs
                            };

                            const numLegs = legsToShow.length;
                            const spacingClass = getSpacingClass(numLegs);
                            const paddingClass = getPaddingClass(numLegs);
                            const shouldScroll = numLegs > 5;

                            return (
                                <div className={`${spacingClass} ${shouldScroll ? 'overflow-y-auto max-h-96' : ''}`}>
                                    {legsToShow.map((leg, index) => (
                                        <div key={index} className={`flex items-center justify-between ${paddingClass}`}>
                                    <div className="flex items-center gap-4 sm:gap-6 w-full">
                                        <i className={`self-start text-2xl sm:text-3xl lg:text-4xl zip-icon-mode_${getModeIconClass(leg?.mode)}`}></i>
                                        <div className="flex-1">
                                            <p className="text-base sm:text-lg lg:text-xl font-bold text-black mt-1">
                                                {moment(leg.from?.departure?.scheduledTime).format('HH:mm')} to {getDestinationText(leg, index, nextPlan?.node?.legs || [], destinationName)}
                                            </p>
                                            <p className="text-sm sm:text-base lg:text-lg text-gray-600">
                                                {formatDurationSeconds(leg.duration)} 
                                                {leg?.agency?.name && (
                                                    <span className="text-gray-600 ml-2 sm:ml-4 leading-none">
                                                        {leg.agency.name}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>

                    {/* Right side - QR Code and Send Button */}
                    <div className="flex-shrink-0 flex flex-col items-center">
                        <div className="bg-white p-3 sm:p-6 rounded-lg mb-4">
                            {isLoading ? (
                                <div className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 flex items-center justify-center bg-gray-100 rounded">
                                    <div className="text-center p-4">
                                        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-black mx-auto mb-2"></div>
                                        <p className="text-sm sm:text-base text-gray-600">Generating QR Code...</p>
                                    </div>
                                </div>
                            ) : qrCodeUrl ? (
                                <div 
                                    className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 cursor-pointer hover:shadow-lg transition-shadow"
                                    onClick={() => {
                                        setShowEnlargedQr(true);
                                        // Send analytics for screen sharing
                                        sendAnalyticsEvent('device_ticket-window_qr-shared', {
                                            origin: originName,
                                            destination: destinationName,
                                            mode: selectedMode,
                                            departureTime: startTime?.format('HH:mm'),
                                            arrivalTime: endTime?.format('HH:mm'),
                                            date: startTime?.format('YYYY-MM-DD'),
                                            shareMethod: 'screen_modal',
                                            isStandalone: isStandalone
                                        });
                                    }}
                                    title="Click to enlarge QR code"
                                >
                                    <QRCodeSVG
                                        value={qrCodeUrl}
                                        size="100%"
                                        width="100%"
                                        height="100%"
                                        level="M"
                                        fgColor="#000000"
                                        bgColor="#FFFFFF"
                                    />
                                </div>
                            ) : (
                                <div className="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 flex items-center justify-center bg-gray-100 rounded">
                                    <p className="text-sm sm:text-base text-gray-600 text-center">QR Code unavailable</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Status Panel - Hide in standalone mode */}
                        {!isStandalone && statusMessages.length > 0 && (
                            <div 
                                ref={statusPanelRef}
                                className="w-64 bg-gray-100 rounded-lg p-3 mt-3 max-h-32 overflow-y-auto"
                            >
                                {statusMessages.map((msg, index) => (
                                    <div key={index} className={`text-xs sm:text-sm mb-2 ${
                                        msg.type === 'success' ? 'text-green-600' :
                                        msg.type === 'error' ? 'text-red-600' :
                                        msg.type === 'warning' ? 'text-orange-600' :
                                        'text-gray-700'
                                    }`}>
                                        {msg.message}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Device-Style QR Code Modal */}
                {showEnlargedQr && qrCodeUrl && (
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                        onClick={() => setShowEnlargedQr(false)}
                    >
                        <div 
                            className="bg-white rounded-2xl shadow-2xl max-w-4xl max-h-[90vh] overflow-auto mx-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header - WhatsApp branding */}
                            <div className="p-6 pb-4 border-b border-gray-200">
                                <div className="flex items-center justify-center gap-3">
                                    <img 
                                        src="https://images.journeys.zip/assets/logos/channels/whatsapp_logo_icon.png" 
                                        alt="WhatsApp" 
                                        className="w-6 h-6 lg:w-8 lg:h-8"
                                    />
                                    <h1 className="text-2xl lg:text-3xl font-bold text-black">
                                        Get live updates on WhatsApp
                                    </h1>
                                </div>
                            </div>

                            {/* Device e-ink style layout */}
                            <div className="p-8">
                                {/* Route header - like device screen */}
                                <div className="text-center mb-8">
                                    <h2 className="text-xl lg:text-2xl font-bold text-black mb-1">
                                        {originName} to {destinationName}
                                    </h2>
                                    <p className="text-base lg:text-lg text-gray-600">
                                        {startTime?.format('dddd, DD MMMM YYYY')}
                                    </p>
                                </div>
                                
                                {/* Times and QR - device layout style */}
                                <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12">
                                    {/* Times - vertical layout like device */}
                                    <div className="text-center lg:text-left">
                                        <div className="mb-6">
                                            <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Departure</p>
                                            <p className="text-4xl lg:text-5xl font-bold text-black">
                                                {startTime?.format('HH:mm')}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Arrival</p>
                                            <p className="text-4xl lg:text-5xl font-bold text-black">
                                                {endTime?.format('HH:mm')}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* QR Code - centered like device */}
                                    <div className="flex-shrink-0">
                                        <div className="w-48 h-48 lg:w-64 lg:h-64">
                                            <QRCodeSVG
                                                value={qrCodeUrl}
                                                size="100%"
                                                width="100%"
                                                height="100%"
                                                level="M"
                                                fgColor="#000000"
                                                bgColor="#FFFFFF"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
} 